import React, { Children } from 'react';
import { motion } from 'framer-motion';
const containerVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.1
    }
  }
};
const featureVariants = {
  hidden: {
    opacity: 0,
    y: 30,
    scale: 0.95
  },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.5,
      ease: [0.22, 1, 0.36, 1]
    }
  }
};
function ShieldIcon() {
  return <svg width="36" height="36" viewBox="0 0 36 36" fill="none" aria-hidden="true" data-id="element-14">
      <path d="M18 4L30 10v8c0 8-5.5 14.5-12 16.5C11.5 32.5 6 26 6 18v-8l12-6z" stroke="#FF6B6B" strokeWidth="2" fill="#FF6B6B" fillOpacity="0.1" data-id="element-15" />
      <path d="M13 18l3 3 7-7" stroke="#FF8A5C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-16" />
    </svg>;
}
function BrainIcon() {
  return <svg width="36" height="36" viewBox="0 0 36 36" fill="none" aria-hidden="true" data-id="element-17">
      <path d="M18 6c-3 0-5.5 1.5-7 4-2 0-4 2-4 5s1.5 5 4 5.5c1 2.5 3.5 4.5 7 4.5s6-2 7-4.5c2.5-.5 4-2.5 4-5.5s-2-5-4-5c-1.5-2.5-4-4-7-4z" stroke="#FF6B6B" strokeWidth="2" fill="#FF6B6B" fillOpacity="0.1" data-id="element-18" />
      <path d="M18 10v15M14 14h8M13 19h10" stroke="#FF8A5C" strokeWidth="1.5" strokeLinecap="round" opacity="0.6" data-id="element-19" />
      <circle cx="18" cy="30" r="0" fill="none" data-id="element-20" />
    </svg>;
}
function HeartPulseIcon() {
  return <svg width="36" height="36" viewBox="0 0 36 36" fill="none" aria-hidden="true" data-id="element-21">
      <path d="M18 30S5 22 5 14a6.5 6.5 0 0113-1 6.5 6.5 0 0113 1c0 8-13 16-13 16z" stroke="#FF6B6B" strokeWidth="2" fill="#FF6B6B" fillOpacity="0.1" data-id="element-22" />
      <polyline points="10,18 14,18 16,14 18,22 20,16 22,18 26,18" stroke="#FF8A5C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none" data-id="element-23" />
    </svg>;
}
function CompassIcon() {
  return <svg width="36" height="36" viewBox="0 0 36 36" fill="none" aria-hidden="true" data-id="element-24">
      <circle cx="18" cy="18" r="13" stroke="#FF6B6B" strokeWidth="2" fill="#FF6B6B" fillOpacity="0.1" data-id="element-25" />
      <polygon points="18,8 21,16 18,28 15,16" fill="#FF8A5C" fillOpacity="0.6" stroke="#FF8A5C" strokeWidth="1" data-id="element-26" />
      <circle cx="18" cy="18" r="2" fill="#FF6B6B" data-id="element-27" />
    </svg>;
}
function TrophyIcon() {
  return <svg width="36" height="36" viewBox="0 0 36 36" fill="none" aria-hidden="true" data-id="element-28">
      <path d="M11 6h14v10c0 4-3 7-7 7s-7-3-7-7V6z" stroke="#FF6B6B" strokeWidth="2" fill="#FF6B6B" fillOpacity="0.1" data-id="element-29" />
      <path d="M11 10H7c0 4 2 6 4 6M25 10h4c0 4-2 6-4 6" stroke="#FF8A5C" strokeWidth="1.5" strokeLinecap="round" fill="none" data-id="element-30" />
      <line x1="18" y1="23" x2="18" y2="27" stroke="#FF6B6B" strokeWidth="2" data-id="element-31" />
      <rect x="13" y="27" width="10" height="3" rx="1" stroke="#FF6B6B" strokeWidth="2" fill="#FF8A5C" fillOpacity="0.2" data-id="element-32" />
    </svg>;
}
function NetworkIcon() {
  return <svg width="36" height="36" viewBox="0 0 36 36" fill="none" aria-hidden="true" data-id="element-33">
      <circle cx="18" cy="10" r="4" stroke="#FF6B6B" strokeWidth="2" fill="#FF6B6B" fillOpacity="0.1" data-id="element-34" />
      <circle cx="8" cy="26" r="4" stroke="#FF8A5C" strokeWidth="2" fill="#FF8A5C" fillOpacity="0.1" data-id="element-35" />
      <circle cx="28" cy="26" r="4" stroke="#FF6B6B" strokeWidth="2" fill="#FF6B6B" fillOpacity="0.1" data-id="element-36" />
      <line x1="18" y1="14" x2="10" y2="23" stroke="#FF6B6B" strokeWidth="1.5" opacity="0.5" data-id="element-37" />
      <line x1="18" y1="14" x2="26" y2="23" stroke="#FF6B6B" strokeWidth="1.5" opacity="0.5" data-id="element-38" />
      <line x1="12" y1="26" x2="24" y2="26" stroke="#FF8A5C" strokeWidth="1.5" opacity="0.5" data-id="element-39" />
    </svg>;
}
interface FeatureBadgeProps {
  title: string;
  description: string;
  icon: React.ReactNode;
  index: number;
}
function FeatureBadge({
  title,
  description,
  icon,
  index
}: FeatureBadgeProps) {
  const isOffset = index % 3 === 1;
  return <motion.div variants={featureVariants} className={`group ${isOffset ? 'md:translate-y-8' : ''}`} data-id="element-40">
      <div className="relative bg-navy-light/70 backdrop-blur-sm border border-white/5 rounded-2xl p-6 md:p-8 transition-all duration-300 group-hover:scale-[1.04] group-hover:shadow-[0_0_40px_rgba(255,107,107,0.15)] group-hover:border-coral/30 h-full" style={{
      clipPath: 'polygon(15% 0%, 85% 0%, 100% 15%, 100% 85%, 85% 100%, 15% 100%, 0% 85%, 0% 15%)'
    }} data-id="element-41">
        {/* Corner accents */}
        <div className="absolute top-0 left-[15%] w-8 h-[2px] bg-gradient-to-r from-coral/40 to-transparent" aria-hidden="true" data-id="element-42" />
        <div className="absolute top-0 right-[15%] w-8 h-[2px] bg-gradient-to-l from-coral/40 to-transparent" aria-hidden="true" data-id="element-43" />

        <div className="flex flex-col items-center text-center pt-2" data-id="element-44">
          <div className="w-16 h-16 rounded-2xl bg-coral/10 flex items-center justify-center mb-5 transition-colors duration-300 group-hover:bg-coral/20" data-id="element-45">
            {icon}
          </div>
          <h3 className="font-display font-bold text-sm md:text-base text-white mb-3 tracking-wide" data-id="element-46">
            {title}
          </h3>
          <p className="font-body text-slate-400 text-xs md:text-sm leading-relaxed" data-id="element-47">
            {description}
          </p>
        </div>
      </div>
    </motion.div>;
}
export function Features() {
  return <section id="features" className="w-full bg-navy-mid py-20 md:py-28 px-6" data-id="element-48">
      <div className="max-w-6xl mx-auto" data-id="element-49">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-80px'
      }} transition={{
        duration: 0.6
      }} className="text-center mb-14" data-id="element-50">
          <h2 className="font-display font-bold text-2xl sm:text-3xl md:text-4xl text-white mb-4" data-id="element-51">
            The Viveludique Arsenal
          </h2>
          <p className="font-body text-slate-400 text-base md:text-lg max-w-xl mx-auto" data-id="element-52">
            Six precision-engineered modules that redefine player loyalty.
          </p>
        </motion.div>

        <motion.div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8" variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-60px'
      }} data-id="element-53">
          <FeatureBadge index={0} title="Adaptive Tier Calibration" description="Self-adjusting loyalty tiers that evolve with your player ecosystem in real time — no manual reconfiguration needed." icon={<ShieldIcon data-id="element-55" />} data-id="element-54" />
          <FeatureBadge index={1} title="Sentiment-Driven Rewards" description="Emotional intelligence layer that reads player mood signals to optimize reward timing and maximize positive impact." icon={<BrainIcon data-id="element-57" />} data-id="element-56" />
          <FeatureBadge index={2} title="Predictive Churn Shields" description="Neural early-warning system that activates retention protocols before players disengage — stopping churn at the source." icon={<HeartPulseIcon data-id="element-59" />} data-id="element-58" />
          <FeatureBadge index={3} title="Dynamic Quest Engines" description="Procedurally generated challenge sequences that keep progression feeling fresh, personal, and endlessly engaging." icon={<CompassIcon data-id="element-61" />} data-id="element-60" />
          <FeatureBadge index={4} title="Micro-Milestone Celebrations" description="Dopamine-optimized achievement moments that reinforce positive play patterns and build lasting emotional loyalty." icon={<TrophyIcon data-id="element-63" />} data-id="element-62" />
          <FeatureBadge index={5} title="Neural Reward Mapping" description="Deep learning model that maps individual reward sensitivity across hundreds of dimensions for surgical precision." icon={<NetworkIcon data-id="element-65" />} data-id="element-64" />
        </motion.div>
      </div>
    </section>;
}